import {
  ServiceContent,
  ServiceLabels,
} from "../../../new-reports/types/newReportTypes";
import { enReportServiceType } from "../seviceBuilder/helper";
import { prescribedServiceStatus } from "../common/prescriptionPeriod";
import {
  HistoryServiceDetail,
  HistoryServiceListItem,
  ServiceHistoryRow,
  historyClientName,
} from "../../entities/history";

/**
 * Backend → view translations for History → Programming.
 *
 * The listing endpoint returns flat rows (`toServiceHistoryRow`). The detail
 * endpoint returns the report's own `ServiceContent` — the same object the
 * prescription report and `ServiceViewModal` render, with the values
 * prescribed for this client — so `normalizeServiceContent` only fills in
 * what the view assumes is present (arrays, `rational.list`) and passes the
 * rest through untouched.
 */

export const toServiceHistoryRow = (
  item: HistoryServiceListItem
): ServiceHistoryRow => ({
  id: item.id,
  dateCreated: item.dateCreated,
  clientId: item.customerID,
  clientName: historyClientName(item),
  serviceType:
    enReportServiceType[item.serviceType] ?? `Type ${item.serviceType}`,
  serviceName: item.serviceName || item.logicName || "—",
  status: prescribedServiceStatus(item),
});

const asArray = <T,>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

/**
 * The detail payload as `ServiceContentView` expects it. The view reads
 * `service.details.some(...)` and `service.rational.list` without guards, and
 * the backend sends `rational: null` for a service prescribed by hand.
 */
export const normalizeServiceContent = (
  detail: HistoryServiceDetail
): ServiceContent => ({
  serviceName: detail.serviceName ?? "",
  serviceType:
    detail.serviceType ||
    enReportServiceType[detail.serviceTypeCode] ||
    `Type ${detail.serviceTypeCode}`,
  serviceTypeCode: detail.serviceTypeCode,
  description: detail.description ?? "",
  imageURL: detail.imageURL ?? undefined,
  prescriptionPeriod: {
    startDate: detail.prescriptionPeriod?.startDate ?? "",
    endDate: detail.prescriptionPeriod?.endDate ?? undefined,
  },
  deactivationDate: detail.deactivationDate,
  activeCompounds: asArray(detail.activeCompounds),
  attributes: asArray(detail.attributes),
  pnoeBenefits: asArray(detail.pnoeBenefits),
  additionalBenefits: asArray(detail.additionalBenefits),
  rational: { list: asArray(detail.rational?.list) },
  details: asArray(detail.details),
});

/**
 * Section labels for the detail page. The report payload carries these per
 * language; History has no language switch, so the English set the modal
 * shows is used.
 */
export const SERVICE_HISTORY_LABELS: ServiceLabels & {
  furtherDetails: string;
} = {
  prescriptionPeriod: "Prescription Period",
  activeCompounds: "Active compounds",
  activeSubstancesFormula: "Active substances formula",
  activeSubstances: "Active substances",
  attributes: "Attributes",
  rational: "Rationale",
  conditions: "Condition(s)",
  pnoeBenefits: "PNOE benefits",
  additionalBenefits: "Additional benefits",
  furtherDetails: "Further details",
};

/** `unitType` 1 = metric, matching the report payload's convention. */
export const SERVICE_HISTORY_UNIT_TYPE = 1;
